import { FC, useState } from "react";
import { getProductsBySkus, GetProductsBySkus, GetProductsBySkusResponse, ProductsWithLedgerManagedStatus, ProductWithLedgerManagedStatus } from "domains/products/services/ProductsService";
import { getMultipleProductLedgerManagedStatus, updateManagedProducts,UpdateManagedProductsDto, MultipleManagedProductResponse } from "domains/allocations/services/allocation-service";
import { ManagedProductsTable } from "domains/allocations/components";

import "./AddMultipleProducts.css"

import TextField from '@mui/material/TextField';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Badge, Grid, Typography, Tabs, Tab, Stack } from "@mui/material";
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import localisations from "domains/allocations/localisation";
import { red } from "@mui/material/colors";


type AddMultipleProductsProps = {
    fieldCompanyId: number;
};

type BadgeColor = "primary" | "secondary" | "info" | "success" | "warning" | "error" | "default";

function makeResultsTab(tabValue: any, labelText: string, badgeValue: number, badgeColor: BadgeColor) {
    const tabLabel = <Badge color={badgeColor}
                            badgeContent={badgeValue}
                            sx={{ pr: badgeValue ? 2 : 0 }}
                            children={labelText} />;
    return <Tab label={tabLabel}
                value={tabValue}
                key={tabValue} />;
}

const AddMultipleProducts: FC<AddMultipleProductsProps> = (props) => {
    const { fieldCompanyId } = props;
    const [products, setProducts] = useState("");
    const [fetchedLedgerInfo, setFetchedLedgerInfo] = useState(false);
    const [hasItems, setHasItems] = useState(false);
    const [hasValidItems, setHasValidItems] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [searchedProducts, setSearchedProducts] = useState(false);
    const [tabValue, setTabValue] = useState("1");
    const [isSearching, setIsSearching] = useState(false);
    const [isUpdatingAllProducts, setIsUpdatingAllProducts] = useState(false);

    const [viewModel, setViewModel] = useState<ProductsWithLedgerManagedStatus>({
        ItemsToUpdate : [],
        AlreadyManagedItems : [],
        NotFoundItems: []
    });

    const setUpViewModel = (distinctSkus:string[], ledgerManagedInfo: MultipleManagedProductResponse[], productsInfo: GetProductsBySkusResponse[] ): void =>{
        let viewModel: ProductsWithLedgerManagedStatus = {
            ItemsToUpdate : [],
            AlreadyManagedItems : [],
            NotFoundItems: []
        };
        let index: number = -1000;
        distinctSkus.forEach(sku => {
            let productDetails = productsInfo.find(x => x.sku.toLowerCase() === sku);
            let item: ProductWithLedgerManagedStatus = {
                description: "",
                sku: sku,
                isLedgerManaged: null,
                productId : index
            };
            index++;
            if(productDetails === undefined){
                viewModel.NotFoundItems.push(item);
            }
            else{
                let ledgerManagedStatus = ledgerManagedInfo.find(x => x.productId === productDetails?.id);
                item.productId = productDetails.id;
                item.description = productDetails.description;
                item.isLedgerManaged = !!ledgerManagedStatus?.ledgerManaged;
                if (item.isLedgerManaged) {
                    viewModel.AlreadyManagedItems.push(item);
                } else {
                    viewModel.ItemsToUpdate.push(item);
                }
            }
        });
        setViewModel(viewModel);
    }

    const fetchProductInfoByIds = async (distinctSkus:string[], productsInfo:GetProductsBySkusResponse[]):  Promise<any>  =>{
        let productsIds = productsInfo.map(x => x.id);
        await getMultipleProductLedgerManagedStatus(productsIds)
        .then(response => {
            setSearchedProducts(true);
            setIsSearching(false);
            setFetchedLedgerInfo(true);
            setHasValidItems(true);
            setUpViewModel(distinctSkus, response, productsInfo);
        })
    }

    const searchForProductIds = async ():  Promise<any>  =>{
        setIsSearching(true);
        setSearchedProducts(false);
        let skus: string[] = products.split("\n");
        skus = skus.map(x => x.trim().toLowerCase());
        let distinctSkus: string[] = skus.filter((sku, index) => sku.length > 0 && skus.indexOf(sku) === index);
        let data: GetProductsBySkus = {
            fieldCompanyId: fieldCompanyId,
            skus: distinctSkus
        }
        if(distinctSkus.length <= 50){
            await getProductsBySkus(data)
            .then(response => {
                if(response.length > 0){
                    fetchProductInfoByIds(distinctSkus, response);
                }
                else{
                    setSearchedProducts(true);
                    setIsSearching(false);
                    setHasValidItems(false);
                }
            });
        }
        else{
            setIsSearching(false);
            setErrors([localisations.addMultipleManagedProducts.tooManySkus]);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setTabValue(newValue);
    };

    const updateProducts = async ():Promise<any>  => {
        setIsUpdatingAllProducts(true);
        let productsToUpdate: ProductWithLedgerManagedStatus[] = viewModel?.ItemsToUpdate ?? [];
        let data: UpdateManagedProductsDto = {
            productIds: productsToUpdate.map(x => x.productId as number),
            ledgerManaged: true
        }
        try{
            await updateManagedProducts(data);
            await searchForProductIds();
        }
        catch(ex){
            setErrors([localisations.addMultipleManagedProducts.errorUpdatingProducts]);
        }finally{
            setIsUpdatingAllProducts(false);
        }
    };

    const updateProduct = async (productId: number | null):Promise<any>  =>{
        let productIds : number[] = [];
        productIds.push(productId as number);
        let data: UpdateManagedProductsDto = {
            productIds: productIds,
            ledgerManaged: true
        }
        await updateManagedProducts(data);
        await searchForProductIds();
    };


    return (
        <>
            <Grid item xs={12}>
                <Typography sx={{ pb: 2, mt:-1 }}>{localisations.addMultipleManagedProducts.instructionText}</Typography>
            </Grid>
            <Grid item xs={12}>
                <TextField multiline minRows={2} maxRows={50} value={products} onChange={e => {setProducts(e.target.value);setSearchedProducts(false); setErrors([]); (e.target.value.length > 0) ? setHasItems(true) : setHasItems(false) }} 
                    fullWidth label={localisations.addMultipleManagedProducts.productsSearchLabel} />
            </Grid>
            <Grid sx={{ mb: 2, mt: 2 }} item xs={12}>
                <LoadingButton
                    aria-label={localisations.addMultipleManagedProducts.productsSearchButton}
                    onClick={() => searchForProductIds()}
                    variant="contained"
                    disabled={!hasItems}
                    loading={isSearching}>
                    {localisations.addMultipleManagedProducts.productsSearchButton}
                </LoadingButton>
            </Grid>
            {fetchedLedgerInfo ? (
                <>
                    <Grid item xs={12}>
                        <Typography variant="h5" sx={{ pb: 2, mt:2 }}>{localisations.addMultipleManagedProducts.resultsHeading}</Typography>
                    </Grid>
                    {hasValidItems ? (
                        <>
                            <TabContext value={tabValue}>
                                <Tabs value={tabValue} onChange={handleTabChange}>
                                    {[
                                        makeResultsTab("1", localisations.addMultipleManagedProducts.nonManagedProductsTabHeader, viewModel.ItemsToUpdate.length, "info"),
                                        makeResultsTab("2", localisations.addMultipleManagedProducts.alreadyManagedProductsTabHeader, viewModel.AlreadyManagedItems.length, "success"),
                                        makeResultsTab("3", localisations.addMultipleManagedProducts.notFoundProductsTabHeader, viewModel.NotFoundItems.length, "error")
                                    ]}
                                </Tabs>
                                <TabPanel value="1">
                                    {viewModel.ItemsToUpdate.length > 0 ? (
                                        <>
                                            <Typography sx={{ mb: 2, mt: 2 }}>{localisations.addMultipleManagedProducts.nonManagedProductsText}</Typography>
                                            <ManagedProductsTable data={viewModel.ItemsToUpdate} hasButton={true} buttonClick={updateProduct} />
                                            <Box sx={{ pb: 2, mt: 2 }}>
                                                <LoadingButton
                                                    aria-label={localisations.addMultipleManagedProducts.markProductsAsManagedButtonText}
                                                    onClick={() => updateProducts()}
                                                    variant="contained"
                                                    disabled={isUpdatingAllProducts}
                                                    loading={isUpdatingAllProducts}>
                                                    {localisations.addMultipleManagedProducts.markProductsAsManagedButtonText}
                                                </LoadingButton>
                                                <Typography sx={{ mb: 2, mt:2 }}>{localisations.addMultipleManagedProducts.markProductsAsManagedExplanation}</Typography>
                                            </Box>
                                        </>
                                    ) : (
                                        <Typography sx={{ pb: 2, mt:2 }}>{localisations.addMultipleManagedProducts.noNonManagedProductsText}</Typography>
                                    )}
                                </TabPanel>
                                <TabPanel value="2">
                                    {viewModel.AlreadyManagedItems.length > 0 ? (
                                        <>
                                            <Typography sx={{ mb: 2, mt:2 }}>{localisations.addMultipleManagedProducts.alreadyManagedProductsText}</Typography>
                                            <ManagedProductsTable data={viewModel.AlreadyManagedItems} hasButton={false}  />
                                        </>
                                    ) : (
                                        <Typography sx={{ pb: 2, mt:2 }}>{localisations.addMultipleManagedProducts.noAlreadyManagedProductsText}</Typography>
                                    )}
                                </TabPanel>
                                <TabPanel value="3">
                                    {viewModel.NotFoundItems.length > 0 ? (
                                        <>
                                            <Typography sx={{ mb: 2, mt:2}}>{localisations.addMultipleManagedProducts.notFoundProductsText}</Typography>
                                            <ManagedProductsTable data={viewModel.NotFoundItems} hasButton={false}  />
                                        </>
                                    ) : (
                                        <Typography sx={{ pb: 2, mt:2 }}>{localisations.addMultipleManagedProducts.noNotFoundProductsText}</Typography>
                                    )}
                                </TabPanel>
                            </TabContext>
                        </>
                    ) : (
                        <Typography sx={{ pb: 2, mt:-1 }}>{localisations.addMultipleManagedProducts.noValidItems}</Typography>
                    )}
                </>
            ) : (
                searchedProducts && (
                    <Typography sx={{ pb: 2, mt:2, color:red[500] }}>{localisations.addMultipleManagedProducts.noProductsFound}</Typography>
                )
            )}
            {errors.length > 0 && (
                <Grid item xs={12}>
                    <Stack spacing={1} sx={{ mt:0}}>
                    {errors.map(text => {
                        return (
                            <Typography  sx={{ pb: 2, mt:2, color:red[500] }}>{text}</Typography>
                        );
                    })}
                    </Stack>
                </Grid>
            )}
        </>
    );
};

export default AddMultipleProducts;
